import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { HrTaskResponse } from "../tasklist/types";
import {
  fetchHrTasklist,
  fetchMoreHrTasks,
  hrTaskAcknowledge,
  talentsDashboardCounts,
} from "./dashboardActions";
import { Slice, SliceState, TalentCountsState } from "./types";

const initialHrTaskListState: Slice = {
  status: "idle",
  ids: [],
  entities: {},
  error: null,
  cursor: undefined,
};

const initialTalentsState: TalentCountsState = {
  counts: null,
  status: "idle",
};

const initialSliceState: SliceState = {
  status: "idle",
  lastFetched: null,
  hrTasklist: initialHrTaskListState,
  talentCounts: initialTalentsState,
};

export const hrTasklistAdapter = createEntityAdapter<HrTaskResponse>();

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    ...initialSliceState,
    hrTasklist: hrTasklistAdapter.getInitialState(initialHrTaskListState),
    talents: initialTalentsState,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHrTasklist.fulfilled, (state, action) => {
        hrTasklistAdapter.setAll(state.hrTasklist, action.payload.rows);
        state.hrTasklist.cursor = action.payload.cursor;
        state.hrTasklist.status = "succeeded";
        state.lastFetched = Date.now();
      })
      .addCase(fetchHrTasklist.pending, (state) => {
        state.hrTasklist.status = "pending";
      })
      .addCase(fetchHrTasklist.rejected, (state) => {
        state.hrTasklist.status = "failed";
      })
      .addCase(fetchMoreHrTasks.fulfilled, (state, action) => {
        hrTasklistAdapter.upsertMany(state.hrTasklist, action.payload.rows);
        state.hrTasklist.cursor = action.payload.cursor;
        state.hrTasklist.status = "succeeded";
        state.lastFetched = Date.now();
      })
      .addCase(fetchMoreHrTasks.pending, (state) => {
        state.hrTasklist.status = "pending";
      })
      .addCase(fetchMoreHrTasks.rejected, (state) => {
        state.hrTasklist.status = "failed";
      })
      .addCase(hrTaskAcknowledge.pending, (state) => {
        state.hrTasklist.status = "pending";
      })
      .addCase(hrTaskAcknowledge.fulfilled, (state, action) => {
        if (action.payload) {
          action.payload.forEach((event) => {
            if (event.name === "hr-task-ack") {
              hrTasklistAdapter.removeOne(
                state.hrTasklist,
                action.meta.arg.hrTaskId,
              );
            }
          });
        }
        state.hrTasklist.status = "succeeded";
      })
      .addCase(hrTaskAcknowledge.rejected, (state) => {
        state.hrTasklist.status = "failed";
      })
      .addCase(talentsDashboardCounts.fulfilled, (state, action) => {
        state.talentCounts.counts = action.payload;
        state.status = "succeeded";
      })
      .addCase(talentsDashboardCounts.pending, (state) => {
        state.status = "pending";
      })
      .addCase(talentsDashboardCounts.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { selectAll: selectAllHrTaskResponses } =
  hrTasklistAdapter.getSelectors(
    (state: RootState) => state.dashboard.hrTasklist,
  );

export const dashboardReducer = dashboardSlice.reducer;
